import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import CustomerAutocomplete from '../CustomerAutocomplete';
import { Customer } from 'types';
import emotionStyled from '@emotion/styled';

const StyledForm = emotionStyled(Form)`
  background-color: #fff;
  color: #212b36;
  transition: box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  box-shadow:
    0 0 2px 0 rgba(145, 158, 171, 0.2),
    0 12px 24px -4px rgba(145, 158, 171, 0.12);
  border-radius: 16px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const InvoiceFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const customerId = searchParams.get('customer_id') ?? '';
  const paid = searchParams.get('paid') ?? '';
  const finalized = searchParams.get('finalized') ?? '';
  const dateFrom = searchParams.get('date_from') ?? '';
  const dateTo = searchParams.get('date_to') ?? '';

  // Sync customer from query params
  useEffect(() => {
    if (!customerId) setCustomer(null);
  }, [customerId]);

  const updateSearchParam = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleCustomerChange = useCallback(
    (cust: Customer | null) => {
      setCustomer(cust);
      updateSearchParam('customer_id', cust?.id.toString() ?? null);
    },
    [updateSearchParam]
  );

  const clearFilters = () => {
    setCustomer(null);
    setSearchParams({});
  };

  return (
    <StyledForm className="mb-3">
      <Row className="g-3">
        <Col md={3}>
          <CustomerAutocomplete
            value={customer}
            onChange={handleCustomerChange}
            isClearable
          />
        </Col>

        <Col md={1}>
          <Form.Select
            value={paid}
            onChange={(e) => updateSearchParam('paid', e.target.value || null)}
          >
            <option value="">Paid?</option>
            <option value="true">Paid</option>
            <option value="false">Unpaid</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Select
            value={finalized}
            onChange={(e) =>
              updateSearchParam('finalized', e.target.value || null)
            }
          >
            <option value="">Finalized?</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Form.Select>
        </Col>

        <Col md={2}>
          <Form.Floating>
            <Form.Control
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) =>
                updateSearchParam('date_from', e.target.value || null)
              }
              placeholder="Date From"
            />
            <label htmlFor="dateFrom">Date From</label>
          </Form.Floating>
        </Col>

        <Col md={2}>
          <Form.Floating>
            <Form.Control
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) =>
                updateSearchParam('date_to', e.target.value || null)
              }
              placeholder="Date To"
            />
            <label htmlFor="dateTo">Date To</label>
          </Form.Floating>
        </Col>

        <Col md="2" className="d-flex justify-content-end">
          <Button variant="outline-secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Col>
      </Row>
    </StyledForm>
  );
};

export default InvoiceFilters;
